// frontend/src/pages/reports/ReportDashboard.js
import React, { useState, useEffect } from 'react';
import {
  Card,
  Row,
  Col,
  Statistic,
  Typography,
  Spin,
  message,
  Modal,
  Table,
  Select,
} from 'antd';
import { BarChart, Bar, XAxis, YAxis, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import apiService from '../../services/api';
import { COLOR_PRIMARY, COLOR_ERROR } from '../../utils/colors';

const { Title, Text } = Typography;
const { Option } = Select;

const CURRENT_YEAR = 2026;
const YEAR_OPTIONS = Array.from({ length: 5 }, (_, i) => CURRENT_YEAR - i);

const ReportDashboard = () => {
  const [selectedYear, setSelectedYear] = useState(CURRENT_YEAR);
  const [financialStats, setFinancialStats] = useState({
    totalIncome: 0,
    totalExpenses: 0,
    netIncome: 0,
    incomeJobsCount: 0
  });
  const [incomeByChannel, setIncomeByChannel] = useState([]);
  const [receivedIncomeByChannel, setReceivedIncomeByChannel] = useState([]);
  const [expenseByCategory, setExpenseByCategory] = useState([]);
  const [paidExpenseByCategory, setPaidExpenseByCategory] = useState([]);
  const [unpaidIncomeByMonth, setUnpaidIncomeByMonth] = useState([]);
  const [unpaidExpenseByMonth, setUnpaidExpenseByMonth] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalVisible, setModalVisible] = useState(false);
  const [modalTitle, setModalTitle] = useState('');
  const [unpaidPayments, setUnpaidPayments] = useState([]);
  const [loadingPayments, setLoadingPayments] = useState(false);
  const [currentModalMonth, setCurrentModalMonth] = useState(null);
  const [currentModalPlPosition, setCurrentModalPlPosition] = useState(null);

  useEffect(() => {
    const fetchDashboardSummary = async () => {
      try {
        const res = await apiService.reports.getDashboardSummary({ year: selectedYear });
        setFinancialStats(res.data.financialStats || {
          totalIncome: 0,
          totalExpenses: 0,
          netIncome: 0,
          incomeJobsCount: 0
        });
        setIncomeByChannel(Object.entries(res.data.incomeByChannel || {}).map(([name, value]) => ({ name, value })));
        setReceivedIncomeByChannel(Object.entries(res.data.receivedIncomeByChannel || {}).map(([name, value]) => ({ name, value })));
        setExpenseByCategory(Object.entries(res.data.expenseByCategory || {}).map(([name, value]) => ({ name, value })));
        setPaidExpenseByCategory(Object.entries(res.data.paidExpenseByCategory || {}).map(([name, value]) => ({ name, value })));
        
        // Process monthly unpaid payment data
        const incomeMonthly = Object.entries(res.data.unpaidIncomeByMonth || {})
          .map(([month, count]) => ({ month, count: Number(count) }))
          .sort((a, b) => a.month.localeCompare(b.month));
        setUnpaidIncomeByMonth(incomeMonthly);
        
        const expenseMonthly = Object.entries(res.data.unpaidExpenseByMonth || {})
          .map(([month, count]) => ({ month, count: Number(count) }))
          .sort((a, b) => a.month.localeCompare(b.month));
        setUnpaidExpenseByMonth(expenseMonthly);
      } catch (err) {
        message.error('Failed to fetch dashboard summary');
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetchDashboardSummary();
  }, [selectedYear]);

  // Helper function to round to 2 decimal places
  const roundToTwoDecimals = (value) => {
    return Number(Number(value || 0).toFixed(2));
  };

  // Combine income data to show paid and unpaid portions
  const incomeByChannelWithPaidUnpaid = incomeByChannel.map(channel => {
    const paidChannel = receivedIncomeByChannel.find(c => c.name === channel.name);
    const paid = roundToTwoDecimals(paidChannel ? paidChannel.value : 0);
    const total = roundToTwoDecimals(channel.value);
    const unpaid = Math.max(0, roundToTwoDecimals(total - paid));
    return {
      name: channel.name,
      paid: paid,
      unpaid: unpaid
    };
  });

  // Custom tooltip formatter to show 2 decimal places
  const formatTooltipValue = (value) => {
    return `£${roundToTwoDecimals(value).toFixed(2)}`;
  };

  // Custom axis formatter to show 2 decimal places
  const formatAxisValue = (value) => {
    return roundToTwoDecimals(value).toFixed(2);
  };

  // Format month for display (YYYY-MM -> MMM YYYY)
  const formatMonth = (monthStr) => {
    if (!monthStr) return '';
    const [year, month] = monthStr.split('-');
    const date = new Date(year, parseInt(month) - 1);
    return date.toLocaleDateString('en-GB', { month: 'short', year: 'numeric' });
  };

  // Handle bar click to show unpaid payments
  const handleBarClick = async (data, plPosition) => {
    if (!data || !data.month) return;
    
    setLoadingPayments(true);
    setModalVisible(true);
    setModalTitle(`Unpaid ${plPosition === 'income' ? 'Income' : 'Expense'} Payments - ${formatMonth(data.month)}`);
    setCurrentModalMonth(data.month);
    setCurrentModalPlPosition(plPosition);
    
    try {
      // Use the dedicated endpoint for unpaid payments by month
      const params = {
        month: data.month, // YYYY-MM format
        plPosition: plPosition,
      };
      
      const response = await apiService.reports.getUnpaidPaymentsByMonth(params);
      const paymentsData = response?.data?.data || [];
      
      setUnpaidPayments(paymentsData);
    } catch (err) {
      message.error('Failed to fetch unpaid payments');
      console.error(err);
      setUnpaidPayments([]);
    } finally {
      setLoadingPayments(false);
    }
  };

  // Handle status update
  const handleStatusUpdate = async (paymentId, newStatus) => {
    try {
      setLoadingPayments(true);
      await apiService.payments.updateStatus(paymentId, { status: newStatus });
      message.success('Payment status updated successfully');
      
      // Refresh the unpaid payments list if we have the context
      if (currentModalMonth && currentModalPlPosition) {
        const params = {
          month: currentModalMonth,
          plPosition: currentModalPlPosition,
        };
        
        const response = await apiService.reports.getUnpaidPaymentsByMonth(params);
        const paymentsData = response?.data?.data || [];
        setUnpaidPayments(paymentsData);
      }
      
      // Refresh dashboard summary to update bar counts
      const res = await apiService.reports.getDashboardSummary({ year: selectedYear });
      const incomeMonthly = Object.entries(res.data.unpaidIncomeByMonth || {})
        .map(([month, count]) => ({ month, count: Number(count) }))
        .sort((a, b) => a.month.localeCompare(b.month));
      setUnpaidIncomeByMonth(incomeMonthly);
      
      const expenseMonthly = Object.entries(res.data.unpaidExpenseByMonth || {})
        .map(([month, count]) => ({ month, count: Number(count) }))
        .sort((a, b) => a.month.localeCompare(b.month));
      setUnpaidExpenseByMonth(expenseMonthly);
    } catch (err) {
      message.error('Failed to update payment status');
      console.error(err);
    } finally {
      setLoadingPayments(false);
    }
  };

  // Table columns for unpaid payments
  const paymentColumns = [
    {
      title: 'Reference',
      dataIndex: 'reference',
      key: 'reference',
      width: 150,
    },
    {
      title: 'Job Reference',
      dataIndex: ['job', 'bookingReference'],
      key: 'jobReference',
      render: (ref, record) => record.job?.bookingReference || 'N/A',
      width: 150,
    },
    {
      title: 'Amount',
      dataIndex: 'totalAmount',
      key: 'totalAmount',
      render: (amount) => `£${roundToTwoDecimals(amount || 0).toFixed(2)}`,
      width: 120,
      align: 'right',
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status, record) => {
        return (
          <Select
            value={status}
            onChange={(newStatus) => handleStatusUpdate(record._id, newStatus)}
            style={{ width: 130 }}
            size="small"
          >
            <Option value="pending">Pending</Option>
            <Option value="paid">Paid</Option>
            <Option value="overdue">Overdue</Option>
            <Option value="cancelled">Cancelled</Option>
          </Select>
        );
      },
      width: 150,
    },
    {
      title: 'Category',
      dataIndex: 'category',
      key: 'category',
      width: 120,
    },
    {
      title: 'Due Date',
      dataIndex: 'dueDate',
      key: 'dueDate',
      render: (date) => date ? new Date(date).toLocaleDateString('en-GB') : 'N/A',
      width: 120,
    },
    {
      title: 'Created',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (date) => date ? new Date(date).toLocaleDateString('en-GB') : 'N/A',
      width: 120,
    },
  ];

  if (loading) {
    return (
      <div style={{ padding: '24px', textAlign: 'center' }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div style={{ padding: '24px' }}>
      <div style={{ marginBottom: '24px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '12px' }}>
        <div>
          <Title level={2} style={{ marginBottom: 0 }}>Reports Dashboard</Title>
          <Text type="secondary">View and analyze business performance metrics</Text>
        </div>
        <Select
          value={selectedYear}
          onChange={setSelectedYear}
          style={{ width: 120 }}
          size="large"
        >
          {YEAR_OPTIONS.map(y => (
            <Option key={y} value={y}>{y}</Option>
          ))}
        </Select>
      </div>



      <Row gutter={[16, 16]}>
        <Col xs={24} md={4}>
          <Card>
            <Statistic
              title="Total Income"
              value={financialStats.totalIncome}
              precision={2}
              valueStyle={{ color: '#3f8600' }}
              prefix="£"
            />
            <div style={{ color: '#888', fontSize: 13, marginTop: 4 }}>
              VAT: £{(financialStats.totalIncomeVAT || 0).toFixed(2)}
            </div>
          </Card>
        </Col>
        <Col xs={24} md={4}>
          <Card>
            <Statistic
              title="Total Expenses"
              value={financialStats.totalExpenses}
              precision={2}
              valueStyle={{ color: '#cf1322' }}
              prefix="£"
            />
            <div style={{ color: '#888', fontSize: 13, marginTop: 4 }}>
              VAT: £{(financialStats.totalExpensesVAT || 0).toFixed(2)}
            </div>
          </Card>
        </Col>
        <Col xs={24} md={4}>
          <Card>
            <Statistic
              title="Net Income"
              value={financialStats.netIncome}
              precision={2}
              valueStyle={{ color: financialStats.netIncome >= 0 ? '#3f8600' : '#cf1322' }}
              prefix="£"
            />
            <div style={{ color: '#888', fontSize: 13, marginTop: 4 }}>
              VAT: £{((financialStats.totalIncomeVAT || 0) - (financialStats.totalExpensesVAT || 0)).toFixed(2)}
            </div>
          </Card>
        </Col>
        <Col xs={24} md={4}>
          <Card>
            <Statistic
              title="Income Jobs"
              value={financialStats.incomeJobsCount}
              valueStyle={{ color: COLOR_PRIMARY }}
            />
            <div style={{ color: '#888', fontSize: 13, marginTop: 4 }}>
              &nbsp;
            </div>
          </Card>
        </Col>
        <Col xs={24} md={4}>
          <Card>
            <Statistic
              title="Received Income"
              value={financialStats.paidIncome}
              precision={2}
              valueStyle={{ color: '#3f8600' }}
              prefix="£"
            />
            <div style={{ color: '#888', fontSize: 13, marginTop: 4 }}>
              VAT: £{(financialStats.paidIncomeVAT || 0).toFixed(2)}
            </div>
          </Card>
        </Col>
        <Col xs={24} md={4}>
          <Card>
            <Statistic
              title="Paid Expenses"
              value={financialStats.paidExpenses}
              precision={2}
              valueStyle={{ color: '#cf1322' }}
              prefix="£"
            />
            <div style={{ color: '#888', fontSize: 13, marginTop: 4 }}>
              VAT: £{(financialStats.paidExpensesVAT || 0).toFixed(2)}
            </div>
          </Card>
        </Col>
      </Row>

      {/* Replaced Charts */}
      <Row gutter={[16, 16]}>
        <Col xs={24} md={12}>
          <Card title="Income Distribution by Job Channel">
            <ResponsiveContainer width="100%" height={300}>
              <BarChart
                data={incomeByChannelWithPaidUnpaid}
                layout="vertical"
                margin={{ left: 40, right: 20, top: 20, bottom: 20 }}
              >
                <XAxis 
                  type="number" 
                  allowDecimals={true} 
                  tickFormatter={formatAxisValue}
                />
                <YAxis dataKey="name" type="category" width={120} />
                <Tooltip formatter={formatTooltipValue} contentStyle={{ background: '#1f1f1f', border: '1px solid #333', color: '#fff' }} itemStyle={{ color: '#fff' }} labelStyle={{ color: '#fff', fontWeight: 600 }} cursor={false} />
                <Legend />
                <Bar dataKey="paid" stackId="income" fill="#3f8600" name="Paid" />
                <Bar dataKey="unpaid" stackId="income" fill={COLOR_ERROR} name="Unpaid" />
              </BarChart>
            </ResponsiveContainer>
          </Card>
        </Col>
        <Col xs={24} md={12}>
          <Card title="Expense Distribution by Category">
            <ResponsiveContainer width="100%" height={300}>
              <BarChart
                data={expenseByCategory.map(item => ({ ...item, value: roundToTwoDecimals(item.value) }))}
                layout="vertical"
                margin={{ left: 40, right: 20, top: 20, bottom: 20 }}
              >
                <XAxis 
                  type="number" 
                  allowDecimals={true} 
                  tickFormatter={formatAxisValue}
                />
                <YAxis dataKey="name" type="category" width={120} />
                <Tooltip formatter={formatTooltipValue} contentStyle={{ background: '#1f1f1f', border: '1px solid #333', color: '#fff' }} itemStyle={{ color: '#fff' }} labelStyle={{ color: '#fff', fontWeight: 600 }} cursor={false} />
                <Legend />
                <Bar dataKey="value" fill="#FF8042" name="Expense" />
              </BarChart>
            </ResponsiveContainer>
          </Card>
        </Col>
      </Row>
      <Row gutter={[16, 16]}>
        <Col xs={24} md={12}>
          <Card title="Received Income Distribution by Channel">
            <ResponsiveContainer width="100%" height={300}>
              <BarChart
                data={receivedIncomeByChannel.map(item => ({ ...item, value: roundToTwoDecimals(item.value) }))}
                layout="vertical"
                margin={{ left: 40, right: 20, top: 20, bottom: 20 }}
              >
                <XAxis 
                  type="number" 
                  allowDecimals={true} 
                  tickFormatter={formatAxisValue}
                />
                <YAxis dataKey="name" type="category" width={120} />
                <Tooltip formatter={formatTooltipValue} contentStyle={{ background: '#1f1f1f', border: '1px solid #333', color: '#fff' }} itemStyle={{ color: '#fff' }} labelStyle={{ color: '#fff', fontWeight: 600 }} cursor={false} />
                <Legend />
                <Bar dataKey="value" fill="#3f8600" name="Received Income" />
              </BarChart>
            </ResponsiveContainer>
          </Card>
        </Col>
        <Col xs={24} md={12}>
          <Card title="Paid Expense Distribution by Category">
            <ResponsiveContainer width="100%" height={300}>
              <BarChart
                data={paidExpenseByCategory.map(item => ({ ...item, value: roundToTwoDecimals(item.value) }))}
                layout="vertical"
                margin={{ left: 40, right: 20, top: 20, bottom: 20 }}
              >
                <XAxis 
                  type="number" 
                  allowDecimals={true} 
                  tickFormatter={formatAxisValue}
                />
                <YAxis dataKey="name" type="category" width={120} />
                <Tooltip formatter={formatTooltipValue} contentStyle={{ background: '#1f1f1f', border: '1px solid #333', color: '#fff' }} itemStyle={{ color: '#fff' }} labelStyle={{ color: '#fff', fontWeight: 600 }} cursor={false} />
                <Legend />
                <Bar dataKey="value" fill="#cf1322" name="Paid Expense" />
              </BarChart>
            </ResponsiveContainer>
          </Card>
        </Col>
      </Row>
      <Row gutter={[16, 16]}>
        <Col xs={24} md={12}>
          <Card title="Unpaid Income Payments per Month (Click bar to view details)">
            <ResponsiveContainer width="100%" height={300}>
              <BarChart
                data={unpaidIncomeByMonth.map(item => ({ ...item, monthLabel: formatMonth(item.month) }))}
                margin={{ left: 20, right: 20, top: 20, bottom: 20 }}
                barSize={25}
                onClick={(chartEvent) => {
                  if (
                    chartEvent &&
                    chartEvent.activePayload &&
                    chartEvent.activePayload[0] &&
                    chartEvent.activePayload[0].payload
                  ) {
                    handleBarClick(chartEvent.activePayload[0].payload, 'income');
                  }
                }}
              >
                <XAxis 
                  dataKey="monthLabel" 
                  angle={-45}
                  textAnchor="end"
                  height={80}
                />
                <YAxis allowDecimals={false} />
                <Tooltip
                  formatter={(value) => [`${value} payments`, 'Unpaid Income']}
                  labelFormatter={(label) => `Month: ${label}`}
                  contentStyle={{ background: '#1f1f1f', border: '1px solid #333', color: '#fff' }} itemStyle={{ color: '#fff' }} labelStyle={{ color: '#fff', fontWeight: 600 }} cursor={false}
                />
                <Legend />
                <Bar
                  dataKey="count"
                  fill={COLOR_ERROR}
                  name="Unpaid Income"
                  style={{ cursor: 'pointer' }}
                />
              </BarChart>
            </ResponsiveContainer>
          </Card>
        </Col>
        <Col xs={24} md={12}>
          <Card title="Unpaid Expense Payments per Month (Click bar to view details)">
            <ResponsiveContainer width="100%" height={300}>
              <BarChart
                data={unpaidExpenseByMonth.map(item => ({ ...item, monthLabel: formatMonth(item.month) }))}
                margin={{ left: 20, right: 20, top: 20, bottom: 20 }}
                barSize={25}
                onClick={(chartEvent) => {
                  if (
                    chartEvent &&
                    chartEvent.activePayload &&
                    chartEvent.activePayload[0] &&
                    chartEvent.activePayload[0].payload
                  ) {
                    handleBarClick(chartEvent.activePayload[0].payload, 'expense');
                  }
                }}
              >
                <XAxis 
                  dataKey="monthLabel" 
                  angle={-45}
                  textAnchor="end"
                  height={80}
                />
                <YAxis allowDecimals={false} />
                <Tooltip
                  formatter={(value) => [`${value} payments`, 'Unpaid Expense']}
                  labelFormatter={(label) => `Month: ${label}`}
                  contentStyle={{ background: '#1f1f1f', border: '1px solid #333', color: '#fff' }} itemStyle={{ color: '#fff' }} labelStyle={{ color: '#fff', fontWeight: 600 }} cursor={false}
                />
                <Legend />
                <Bar
                  dataKey="count"
                  fill="#cf1322"
                  name="Unpaid Expense"
                  style={{ cursor: 'pointer' }}
                />
              </BarChart>
            </ResponsiveContainer>
          </Card>
        </Col>
      </Row>

      {/* Modal for unpaid payments list */}
      <Modal
        title={modalTitle}
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        footer={null}
        width={1000}
      >
        <Spin spinning={loadingPayments}>
          <Table
            columns={paymentColumns}
            dataSource={unpaidPayments}
            rowKey="_id"
            pagination={{
              pageSize: 10,
              showSizeChanger: true,
              showTotal: (total) => `Total: ${total} unpaid payments`,
            }}
            scroll={{ x: 800 }}
          />
        </Spin>
      </Modal>
    </div>
  );
};

export default ReportDashboard;